import type { OperationsAgeBinDto, OperationsLensKey, OperationsPenKey, OperationsSummaryDto } from "@/api/endpoints";
import { InfoTooltip } from "@/components/InfoTooltip";
import { WORK_TYPE_GROUPS, workTypeGroup, type WorkTypeGroup } from "../operationsLabels";
import { LensTag } from "./LensTags";

export type AgeColorBy = "workType" | "attention";

interface Props {
  age: OperationsSummaryDto["age"];
  colorBy: AgeColorBy;
  onColorBy: (mode: AgeColorBy) => void;
  activeBin?: number;
  /** The lens the bins were cut with; the Attention colouring is then one colour and is disabled. */
  lens?: OperationsLensKey;
  /** The second lens (who holds the pen, 2026-10-28): the bins are cut, the Attention colouring stays. */
  pen?: OperationsPenKey;
  openNow?: number;
  onDrill: (bin: number) => void;
}

const AGE_TIP =
  "How long each open journey has been running, in business days since its first record (the intake when there is one). " +
  "One bar per day up to 30, then one bar for everything older. Colour by work type or by attention. Same count as the Flow card's Open now.";

/** Group totals for one bin (Other absorbs every type outside the three named groups). */
function groupCounts(bin: OperationsAgeBinDto): Map<WorkTypeGroup["key"], number> {
  const out = new Map<WorkTypeGroup["key"], number>();
  for (const [workType, n] of Object.entries(bin.byWorkType)) {
    const key = workTypeGroup(workType).key;
    out.set(key, (out.get(key) ?? 0) + n);
  }
  return out;
}

/**
 * Panel 5: open work by age, 1..30 and 30+, coloured by work-type group or by attention, with observed p50 / p85.
 * One journey per consultant (aged from its earliest record) plus the other open requests, like the Flow card.
 */
export function AgeHistogram({ age, colorBy: requestedColorBy, onColorBy, activeBin, lens, pen, openNow, onDrill }: Props) {
  const colorBy: AgeColorBy = lens ? "workType" : requestedColorBy;
  const bins: OperationsAgeBinDto[] = age.binsBusinessDays;
  const p50 = age.p50Bd;
  const p85 = age.p85Bd;
  const unitLabel = "business days";
  const binCount = bins.length;
  // The axis follows bins 1..30; the overflow bar is capped and labelled when it is taller.
  const dayBins = bins.slice(0, Math.max(0, binCount - 1));
  const max = Math.max(1, ...dayBins.map((b) => b.total));
  const yTop = Math.max(5, Math.ceil(max / 5) * 5);
  const h = (v: number) => `${Math.min(100, (v / yTop) * 100).toFixed(2)}%`;
  const markLeft = (v: number) => {
    const idx = v > binCount - 1 ? binCount : Math.min(Math.max(v, 1), binCount - 1);
    return ((idx - 0.5) / binCount) * 100;
  };
  const groupsInUse = WORK_TYPE_GROUPS.filter((g) => bins.some((b) => (groupCounts(b).get(g.key) ?? 0) > 0));

  return (
    <section className="ops-panel ops-span-8" aria-labelledby="ops-age-h">
      <h3 id="ops-age-h">
        Open items by age
        <span className="sub">{lens || pen ? `${age.sample} of ${openNow ?? age.sample} open` : `${age.sample} open`} · {unitLabel} since the journey started</span>
        <InfoTooltip inline text={AGE_TIP} />
        <LensTag lens={lens} pen={pen} />
        <span className="unit-toggle ops-age-toggle" role="tablist" aria-label="Colour by">
          <button type="button" role="tab" aria-selected={colorBy === "workType"} className={colorBy === "workType" ? "on" : ""} onClick={() => onColorBy("workType")}>
            Work type
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={colorBy === "attention"}
            className={colorBy === "attention" ? "on" : ""}
            disabled={!!lens}
            title={lens ? "Under a lens every bar is one state, so this view is off." : undefined}
            onClick={() => onColorBy("attention")}
          >
            Attention
          </button>
        </span>
        <span className="ops-legend">
          {colorBy === "workType" ? (
            groupsInUse.map((g) => (
              <span key={g.key}><i className={`ops-sw${g.hatch ? " ops-hatch" : ""}`} style={g.hatch ? undefined : { background: g.color }} />{g.label}</span>
            ))
          ) : (
            <>
              <span><i className="ops-sw" style={{ background: "var(--ops-att-stuck)" }} />Stuck</span>
              <span><i className="ops-sw" style={{ background: "var(--ops-att-blocked)" }} />Blocked</span>
              <span><i className="ops-sw" style={{ background: "var(--ops-att-both)" }} />Stuck + Blocked</span>
              <span><i className="ops-sw" style={{ background: "var(--ops-att-clear)", border: "1px solid var(--border-subtle)" }} />Clear</span>
            </>
          )}
        </span>
      </h3>

      {age.sample > 0 && (
        <div className="ops-age-ruler">
          <div className={`tick${markLeft(p50) > 88 ? " edge" : ""}`} style={{ left: `${markLeft(p50).toFixed(2)}%` }}>
            <span>{`p50 observed, ${Math.round(p50)} days`}</span><i aria-hidden="true" />
          </div>
          <div className={`tick${markLeft(p85) > 88 ? " edge" : ""}`} style={{ left: `${markLeft(p85).toFixed(2)}%` }}>
            <span>{`p85 observed, ${Math.round(p85)} days`}</span><i aria-hidden="true" />
          </div>
        </div>
      )}

      <div className="ops-hist">
        <div className="yax"><span>{yTop}</span><span>{yTop / 2}</span><span>0</span></div>
        <div className="plot">
          <div className="grid-lines"><i style={{ top: 0 }} /><i style={{ top: "50%" }} /></div>
          <div className="cols">
            {bins.map((b, i) => {
              const isLast = i === binCount - 1;
              const capped = b.total > yTop;
              const counts = groupCounts(b);
              // The bin's stuck / blocked counts include the items that are both; split them back out for the stack.
              const both = b.stuck + b.blocked - b.stuckOrBlocked;
              const stuckOnly = b.stuck - both;
              const blockedOnly = b.blocked - both;
              const title = colorBy === "attention"
                ? `${b.label} ${unitLabel}: ${b.total} open, ${stuckOnly} stuck, ${blockedOnly} blocked, ${both} both`
                : `${b.label} ${unitLabel}: ${b.total} open`;
              return (
                <button
                  key={b.bin}
                  type="button"
                  className={`col${isLast ? " last" : ""}${activeBin === b.bin ? " on" : ""}`}
                  title={title}
                  onClick={() => onDrill(b.bin)}
                  aria-label={`Age ${b.label}: ${b.total} open`}
                >
                  {colorBy === "workType"
                    ? WORK_TYPE_GROUPS.map((g) => {
                        const n = counts.get(g.key) ?? 0;
                        return n > 0 ? (
                          <span key={g.key} className={`st${g.hatch ? " ops-hatch" : ""}`} style={g.hatch ? { height: h(n) } : { height: h(n), background: g.color }} />
                        ) : null;
                      })
                    : (
                      <>
                        {stuckOnly > 0 && <span className="st" style={{ height: h(stuckOnly), background: "var(--ops-att-stuck)" }} />}
                        {blockedOnly > 0 && <span className="st" style={{ height: h(blockedOnly), background: "var(--ops-att-blocked)" }} />}
                        {both > 0 && <span className="st" style={{ height: h(both), background: "var(--ops-att-both)" }} />}
                        {b.total - b.stuckOrBlocked > 0 && <span className="st" style={{ height: h(b.total - b.stuckOrBlocked), background: "var(--ops-att-clear)" }} />}
                      </>
                    )}
                  {capped && (
                    <>
                      <span className="capmark" aria-hidden="true" />
                      <span className="capn">{b.total} ↑</span>
                    </>
                  )}
                </button>
              );
            })}
          </div>
          <div className="xax">
            {bins.map((b, i) => (
              <span key={b.bin} className={i === binCount - 1 ? "last" : ""}>{b.bin % 2 === 1 || i === binCount - 1 ? b.label : ""}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
